import { useEffect, useState } from "react";
import HomeDiscovery from "../components/HomeDiscovery";
import PageLayout from "../components/PageLayout";
import FeedTable from "../components/FeedTable";
import GreenieBoardTable, { TrapTile } from "../components/GreenieBoardTable";
import { fetchFeed } from "../services/feedService";
import { FeedItem } from "../types/feed";
import { fetchRecentAchievements, RecentAchievementItem } from "../services/achievementsService";
import NextUnlocks from "../components/NextUnlocks";
import PilotSummary from "../components/PilotSummary";
import WeeklyStatsBar, { computeWeeklyStats, WeeklyStats } from "../components/WeeklyStatsBar";
import { useLens } from "../lens/LensProvider";
import { GAUGES } from "../constants/gauges";
import { fetchGaugeLeaderboard, GaugeLeaderboardItem } from "../services/gaugesService";
import { fetchGreenieBoard } from "../services/greenieBoardService";
import { fetchServers, ServerStatus } from "../services/serversService";
import { GreeniePilot } from "../types/greenieBoard";
import { formatRelativeTime } from "../utils/formatters";
import { ACHIEVEMENT_ICONS, DEFAULT_ACHIEVEMENT_ICON } from "../constants/achievementIcons";

const DASHBOARD_RECENT_LIMIT = 5;
const GREENIE_DASHBOARD_PILOT_LIMIT = 5;

const GREENIE_LEGEND: Array<{ key: string; label: string; color: string; textColor: string; desc: string; night?: boolean; wire?: boolean }> = [
  { key: "_OK_", label: "_OK_", color: "#22c55e", textColor: "#dcfce7", desc: "Perfect pass" },
  { key: "OK",   label: "OK",   color: "#166534", textColor: "#bbf7d0", desc: "Good / Fair pass" },
  { key: "--",   label: "–",    color: "#334155", textColor: "#94a3b8", desc: "No grade" },
  { key: "B",    label: "B",    color: "#64748b", textColor: "#94a3b8", desc: "Bolter" },
  { key: "WO",   label: "WO",   color: "#64748b", textColor: "#94a3b8", desc: "Wave-off" },
  { key: "C",    label: "C",    color: "#b52929", textColor: "#fecaca", desc: "Cut — dangerous" },
  { key: "night", label: "OK",  color: "#166534", textColor: "#bbf7d0", night: true, desc: "Night landing" },
  { key: "wire",  label: "3",   color: "#15803d", textColor: "rgba(255,255,255,0.55)", wire: true, desc: "Wire #" },
];

interface HighScoreRow {
  gaugeId: string;
  label: string;
  leader: GaugeLeaderboardItem | null;
  valueLabel: string;
}

export default function DashboardPage() {
  const { pinnedUid } = useLens();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null);
  const [latestProficiencies, setLatestProficiencies] = useState<FeedItem[]>([]);
  const [latestAchievements, setLatestAchievements] = useState<RecentAchievementItem[]>([]);
  const [latestActivity, setLatestActivity] = useState<FeedItem[]>([]);
  const [greeniePilots, setGreeniePilots] = useState<GreeniePilot[]>([]);
  const [highScores, setHighScores] = useState<HighScoreRow[]>([]);
  const [greenieContainerEl, setGreenieContainerEl] = useState<HTMLDivElement | null>(null);
  const [greenieTrapLimit, setGreenieTrapLimit] = useState(6);

  useEffect(() => {
    if (!greenieContainerEl) return;

    const updateLimit = () => {
      const width = greenieContainerEl.clientWidth;
      const maxTrapColumns = Math.floor(Math.max(0, width - 170) / 36);
      setGreenieTrapLimit(Math.max(4, Math.min(12, maxTrapColumns || 6)));
    };

    updateLimit();
    const observer = new ResizeObserver(updateLimit);
    observer.observe(greenieContainerEl);
    return () => observer.disconnect();
  }, [greenieContainerEl]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      setLoading(true);
      setError(null);
      setWeeklyStats(null);
      setLatestProficiencies([]);
      setLatestAchievements([]);
      setLatestActivity([]);
      setGreeniePilots([]);


      try {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);

        const [
          serversResponse,
          proficiencyResponse,
          activityResponse,
          weekFeedResponse,
          recentAchievementsResponse,
          greenieResponse,
          ...leaderboards
        ] = await Promise.all([
          fetchServers().catch(() => ({ servers: [] as ServerStatus[] })),
          fetchFeed({ kind: "proficiency", limit: DASHBOARD_RECENT_LIMIT }),
          fetchFeed({ limit: 10 }),
          fetchFeed({ limit: 500, since: sevenDaysAgo.toISOString() }),
          fetchRecentAchievements({ limit: 5 }),
          fetchGreenieBoard({ sort: "recent", pilot_limit: GREENIE_DASHBOARD_PILOT_LIMIT, trap_limit: 20 }),
          ...GAUGES.map((gauge) => fetchGaugeLeaderboard({ gaugeId: gauge.id, limit: 1 })),
        ]);

        if (cancelled) return;

        setWeeklyStats(computeWeeklyStats(weekFeedResponse.items, serversResponse.servers, sevenDaysAgo));
        setLatestProficiencies(proficiencyResponse.items);
        setLatestActivity(activityResponse.items);
        setLatestAchievements(recentAchievementsResponse.achievements);
        setGreeniePilots(greenieResponse.pilots);

        const highScoreRows = GAUGES.map((gauge, index) => {
          const leader = leaderboards[index]?.items?.[0] ?? null;
          return {
            gaugeId: gauge.id,
            label: gauge.label,
            leader,
            valueLabel: leader ? gauge.format(leader.value) : "No data",
          };
        });
        highScoreRows.sort((a, b) => {
          if (!a.leader?.recorded_at) return 1;
          if (!b.leader?.recorded_at) return -1;
          return new Date(b.leader.recorded_at).getTime() - new Date(a.leader.recorded_at).getTime();
        });
        setHighScores(highScoreRows);

      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Unexpected error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageLayout route="dashboard">
      <HomeDiscovery />
      <div className="dashboard-week">
        <WeeklyStatsBar stats={weeklyStats} loading={loading} personal={false} />
      </div>
      {pinnedUid && <PilotSummary key={pinnedUid} uid={pinnedUid} />}
      {error && (
        <section className="panel panel-tight">
          <div className="alert">{error}</div>
        </section>
      )}

      <div className="dashboard-grid">
        <section className="panel dashboard-card">
          <WidgetHeader title="Latest proficiencies earned" href="#/proficiencies" />
          <RecentProficienciesList items={latestProficiencies} loading={loading} />
        </section>

        <section className="panel dashboard-card">
          <WidgetHeader title="Latest achievements earned" href="#/achievements" />
          <RecentAchievementsList items={latestAchievements} loading={loading} />
        </section>

        <section className="panel dashboard-card">
          <WidgetHeader title="Almost there" href="#/proficiencies" />
          <NextUnlocks heading={false} limit={4} emptyMessage="No proficiency progress yet." />
        </section>

        <section className="panel dashboard-card">
          <WidgetHeader title="Greenie Board" href="#/greenie_board" />
          <div ref={setGreenieContainerEl}>
            {greeniePilots.length > 0 ? (
              <GreenieBoardTable pilots={greeniePilots} trapLimit={greenieTrapLimit} />
            ) : (
              !loading && <div className="dashboard-empty-row">No grading data yet.</div>
            )}
          </div>
          {greeniePilots.length > 0 && (
            <div className="gboard-legend">
              {GREENIE_LEGEND.map((item) => (
                <div key={item.key} className="gboard-legend-item">
                  <div className="gboard-legend-tile">
                    <TrapTile color={item.color} night={!!item.night} size={22} maskId={`dlm-${item.key}`} />
                    {item.wire && <span className="gtrap-wire">{item.label}</span>}
                    {(item.key === "WO" || item.key === "B") && (
                      <span className="gtrap-label" style={{ color: item.textColor }}>{item.label}</span>
                    )}
                  </div>
                  <span className="gboard-legend-desc">{item.desc}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="panel dashboard-card dashboard-card-wide">
          <WidgetHeader title="High score rankings" />
          <div className="dashboard-high-scores-grid">
            {highScores.filter((score) => score.leader != null).map((score) => (
              <div key={score.gaugeId} className="dashboard-high-score-row">
                <div className="dashboard-proficiency-head">
                  <div className="dashboard-high-score-title">{score.label}</div>
                  <span className="dashboard-proficiency-tier">{score.valueLabel}</span>
                </div>
                <div className="dashboard-achievement-meta">
                  <a className="dashboard-high-score-value" href={`#/pilots/${encodeURIComponent(score.leader!.pilot_uid)}`}>
                    {score.leader!.pilot_name || score.leader!.pilot_uid}
                  </a>
                  {score.leader!.recorded_at && <span>{formatRelativeTime(score.leader!.recorded_at)}</span>}
                </div>
              </div>
            ))}
            {!loading && !highScores.some((score) => score.leader != null) && (
              <div className="dashboard-empty-row">No gauge leaderboards yet.</div>
            )}
          </div>
        </section>

        <section className="panel dashboard-card dashboard-card-wide">
          <WidgetHeader title="Latest activity" href="#/feed" />
          <FeedTable items={latestActivity} showPills={false} groupByDate />
        </section>
      </div>
    </PageLayout>
  );
}

function WidgetHeader({ title, href }: { title: string; href?: string }) {
  return (
    <div className="dashboard-widget-header">
      <h2>{title}</h2>
      {href ? <a href={href}>View all</a> : null}
    </div>
  );
}

function RecentAchievementsList({ items, loading }: { items: RecentAchievementItem[]; loading: boolean }) {
  if (loading && items.length === 0) {
    return <div className="status-area"><span className="status">Loading…</span></div>;
  }

  if (items.length === 0) {
    return <div className="dashboard-empty-row">No achievement unlocks yet.</div>;
  }

  return (
    <ul className="dashboard-achievement-list">
      {items.map((item) => (
        <li key={`${item.achievement_id}:${item.pilot_uid}:${item.earned_at}`} className="dashboard-achievement-row">
          <div className="dashboard-achievement-title">
            <span className="dashboard-achievement-icon" aria-hidden="true">
              {ACHIEVEMENT_ICONS[item.achievement_id] ?? DEFAULT_ACHIEVEMENT_ICON}
            </span>
            {item.achievement_name}
          </div>
          <div className="dashboard-achievement-meta">
            {item.pilot_uid ? (
              <a href={`#/achievements?pilot=${encodeURIComponent(item.pilot_uid)}`}>
                {item.pilot_name || item.pilot_uid}
              </a>
            ) : (
              <span>{item.pilot_name || "Unknown pilot"}</span>
            )}
            <span>{item.earned_at ? formatRelativeTime(item.earned_at) : "Unknown time"}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RecentProficienciesList({ items, loading }: { items: FeedItem[]; loading: boolean }) {
  if (loading && items.length === 0) {
    return <div className="status-area"><span className="status">Loading…</span></div>;
  }

  if (items.length === 0) {
    return <div className="dashboard-empty-row">No proficiency events yet.</div>;
  }

  return (
    <ul className="dashboard-achievement-list">
      {items.map((item) => (
        <li key={item.id} className="dashboard-achievement-row">
          <div className="dashboard-proficiency-head">
            <div className="dashboard-achievement-title">{item.title}</div>
            {proficiencyTierLabel(item) && <span className="dashboard-proficiency-tier">{proficiencyTierLabel(item)}</span>}
          </div>
          <div className="dashboard-achievement-meta">
            {item.pilot_uid ? (
              <a href={`#/proficiencies?pilot=${encodeURIComponent(item.pilot_uid)}`}>
                {item.pilot_name || item.pilot_uid}
              </a>
            ) : (
              <span>{item.pilot_name || "Unknown pilot"}</span>
            )}
            <span>{formatRelativeTime(item.occurred_at)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function proficiencyTierLabel(item: FeedItem): string | null {
  const fromSummary = item.summary_vars?.level_display;
  if (typeof fromSummary === "string" && fromSummary.trim()) {
    return fromSummary.trim();
  }

  const level = (item.metadata.level_reached || "").trim();
  if (!level) return null;

  const LEVELS: Record<string, string> = {
    level_1: "Basic Proficiency",
    level_2: "Advanced Proficiency",
    level_3: "Expert Proficiency",
    level_4: "Master Proficiency",
  };

  return LEVELS[level] || `${level} (legacy)`;
}
